import {
  AthletesModel,
  CompetitionFormat,
  getCompetitionFormatFromAthlete,
  getRelevantLiftFields,
  getStatusField,
  LiftField,
  LiftStatus,
  NextLiftData,
} from "./index.js";
import { getRankingCategoryKey } from "./ranking-category.js";
import { Decimal } from "@prisma-local/client/runtime/library";

export function getCurrentAthlete(athletes: AthletesModel[]) {
  const currentLift = getCurrentLift(athletes);
  return getAthleteByLift(athletes, currentLift ?? "squat1");
}

/**
 * Compares two athletes based on their weight for the current lift.
 * @param a - The first athlete.
 * @param b - The second athlete.
 * @param currentLift - The current lift field.
 * @returns A negative number if a is lighter than b, a positive number if a is heavier than b, or 0 if they have the same weight.
 */
export function compareAthletes(
  a: AthletesModel,
  b: AthletesModel,
  currentLift: LiftField,
): number {
  // Handle null/undefined cases
  const aLift = a[currentLift];
  const bLift = b[currentLift];

  if (aLift === null || aLift === undefined) {
    if (bLift === null || bLift === undefined) {
      // Both are null/undefined, compare by PDC then lot
      if (Number(a.pdc) !== Number(b.pdc)) {
        return Number(a.pdc) - Number(b.pdc);
      }
      return a.lot - b.lot;
    }
    return 1; // a is null/undefined, place it at the end
  }

  if (bLift === null || bLift === undefined) {
    return -1; // b is null/undefined, place it at the end
  }

  // Both have valid lifts, compare normally
  if (Number(aLift) !== Number(bLift)) {
    return Number(aLift) - Number(bLift);
  } else if (Number(a.pdc) !== Number(b.pdc)) {
    return Number(a.pdc) - Number(b.pdc);
  } else {
    return a.lot - b.lot;
  }
}

/**
 * Compares two athletes based on their placement.
 * @param a - The first athlete.
 * @param b - The second athlete.
 * @returns A negative number if a is lighter than b, a positive number if a is heavier than b, or 0 if they have the same weight.
 */
export function CompareAthletesByPlacement(
  a: AthletesModel,
  b: AthletesModel,
): number {
  // Place disqualified athletes and those with placement 0 at the bottom
  if (a.isDisqualified && !b.isDisqualified) return 1;
  if (!a.isDisqualified && b.isDisqualified) return -1;
  if (a.placement === 0 && b.placement !== 0) return 1;
  if (a.placement !== 0 && b.placement === 0) return -1;
  if (a.placement === null && b.placement !== null) return 1;
  if (a.placement !== null && b.placement === null) return -1;
  // Normal sorting by placement for other cases
  return (a.placement ?? 0) - (b.placement ?? 0);
}

/**
 * Checks if all lifts are completed for an athlete.
 * @param athlete - The athlete to check.
 * @returns True if all lifts are completed, false otherwise.
 */
export const AreAllLiftsCompleted = (athlete: AthletesModel): boolean => {
  const liftStatuses = [
    athlete.squat1Status,
    athlete.squat2Status,
    athlete.squat3Status,
    athlete.bench1Status,
    athlete.bench2Status,
    athlete.bench3Status,
    athlete.deadlift1Status,
    athlete.deadlift2Status,
    athlete.deadlift3Status,
  ];
  // Check if all statuses are either 'lift' or 'no lift'
  return liftStatuses.every(
    (status) => status === LiftStatus.LIFT || status === LiftStatus.NO_LIFT,
  );
};

/**
 * Retrieves the current lift based on the athletes' status.
 * @param athletes - An array of AthletesModel representing all the athletes.
 * @returns The current lift or null if all lifts have been completed.
 */
export function getCurrentLift(
  athletes: AthletesModel[] | null | undefined,
): LiftField | null {
  if (!athletes || athletes.length === 0) return null;

  const format = getCompetitionFormatFromAthlete(athletes[0]);
  const relevantFields = getRelevantLiftFields(format) as LiftField[];
  for (const lift of relevantFields) {
    const statusField = getStatusField(lift);
    const pending = athletes.filter(
      (athlete) => athlete[statusField] === LiftStatus.ANNONCED,
    );
    if (pending.length) {
      // A later declared round cannot skip a missing earlier declaration.
      return pending.some((athlete) => athlete[lift] != null) ? lift : null;
    }
  }
  return null;
}

export function isLiftStatusUpdate(
  prev: AthletesModel | undefined,
  next: AthletesModel | undefined,
): boolean {
  if (!prev || !next) return false;
  return (
    prev.squat1Status !== next.squat1Status ||
    prev.squat2Status !== next.squat2Status ||
    prev.squat3Status !== next.squat3Status ||
    prev.bench1Status !== next.bench1Status ||
    prev.bench2Status !== next.bench2Status ||
    prev.bench3Status !== next.bench3Status ||
    prev.deadlift1Status !== next.deadlift1Status ||
    prev.deadlift2Status !== next.deadlift2Status ||
    prev.deadlift3Status !== next.deadlift3Status
  );
}

/**
 * Returns true when at least one athlete still has an announced attempt with a load
 * for one of the given lifts (matches getCurrentLift / competition flow).
 */
export function hasActiveAnnouncedLifts(
  athletes: AthletesModel[],
  lifts: LiftField[],
): boolean {
  if (!athletes?.length) return false;
  return lifts.some((lift) =>
    athletes.some(
      (athlete) =>
        athlete[getStatusField(lift)] === LiftStatus.ANNONCED &&
        athlete[lift] != null &&
        athlete[lift] !== undefined,
    ),
  );
}

/** Minimal group fields used to resolve the next group on a plateau. */
export type GroupPlateauScheduling = {
  id: string;
  plateauNumber: number | null;
  groupNumber: number | null;
};

/**
 * Next group on the same plateau (lowest groupNumber strictly greater than current).
 */
export function findNextGroupOnSamePlateau<G extends GroupPlateauScheduling>(
  groups: G[],
  currentGroup: G,
): G | null {
  if (currentGroup.plateauNumber == null || currentGroup.groupNumber == null) {
    return null;
  }
  const currentNum = currentGroup.groupNumber;
  const candidates = groups
    .filter(
      (g) =>
        g.plateauNumber === currentGroup.plateauNumber &&
        g.groupNumber != null &&
        g.groupNumber > currentNum,
    )
    .sort((a, b) => (a.groupNumber ?? 0) - (b.groupNumber ?? 0));
  return candidates[0] ?? null;
}

/**
 * First group (lowest groupNumber) on the given plateau.
 */
export function findFirstGroupOnSamePlateau<G extends GroupPlateauScheduling>(
  groups: G[],
  plateauNumber: number,
): G | null {
  const candidates = groups
    .filter((g) => g.plateauNumber === plateauNumber && g.groupNumber != null)
    .sort((a, b) => (a.groupNumber ?? 0) - (b.groupNumber ?? 0));
  return candidates[0] ?? null;
}

/**
 * First group (by groupNumber) on the plateau immediately after the given one.
 */
export function findFirstGroupOnNextPlateau<G extends GroupPlateauScheduling>(
  groups: G[],
  currentPlateauNumber: number,
): G | null {
  const candidates = groups
    .filter(
      (g) =>
        g.plateauNumber != null &&
        g.plateauNumber > currentPlateauNumber &&
        g.groupNumber != null,
    )
    .sort(
      (a, b) =>
        (a.plateauNumber ?? 0) - (b.plateauNumber ?? 0) ||
        (a.groupNumber ?? 0) - (b.groupNumber ?? 0),
    );
  return candidates[0] ?? null;
}

/**
 * True when currentGroup has the highest groupNumber on its plateau.
 */
export function isLastGroupOnPlateau<G extends GroupPlateauScheduling>(
  allGroups: G[],
  currentGroup: G,
): boolean {
  if (currentGroup.plateauNumber == null || currentGroup.groupNumber == null) {
    return false;
  }
  const plateauGroups = allGroups.filter(
    (g) =>
      g.plateauNumber === currentGroup.plateauNumber && g.groupNumber != null,
  );
  if (plateauGroups.length === 0) return false;
  const maxGroupNumber = Math.max(
    ...plateauGroups.map((g) => g.groupNumber as number),
  );
  return currentGroup.groupNumber === maxGroupNumber;
}

/** Competition movement that just ended on the current group. */
export type MovementPhase = "squat" | "bench" | "deadlift";

/** Per-group movement completion checks for plateau-wide rotation. */
export type GroupMovementPredicates = {
  isGroupSquatOver: (groupId: string) => boolean;
  isGroupBenchOver: (groupId: string) => boolean;
  isGroupDeadliftOver: (groupId: string) => boolean;
  isGroupEmpty?: (groupId: string) => boolean;
};

/**
 * Resolves the next competition group after a movement round ends on the current group.
 * Same movement, same plateau: next group by groupNumber (G1 squat → G2 squat, etc.).
 * All squats done on plateau: wrap to group 1 for bench.
 * All benches done on plateau: wrap to group 1 for deadlift.
 * All deadlifts done on plateau (from last group): first group on next plateau if it exists.
 */
export function resolveNextCompetitionGroup<G extends GroupPlateauScheduling>(
  allGroups: G[],
  currentGroup: G,
  endedMovement: MovementPhase,
  predicates: GroupMovementPredicates,
): G | null {
  if (currentGroup.plateauNumber == null || currentGroup.groupNumber == null) {
    return null;
  }
  allGroups = allGroups.filter(
    (g) =>
      g.plateauNumber != null &&
      g.groupNumber != null &&
      !predicates.isGroupEmpty?.(g.id),
  );
  const plateauGroups = allGroups
    .filter((g) => g.plateauNumber === currentGroup.plateauNumber)
    .sort((a, b) => (a.groupNumber ?? 0) - (b.groupNumber ?? 0));
  const phases: MovementPhase[] = ["squat", "bench", "deadlift"];
  const isOver = {
    squat: predicates.isGroupSquatOver,
    bench: predicates.isGroupBenchOver,
    deadlift: predicates.isGroupDeadliftOver,
  };
  for (const phase of phases.slice(phases.indexOf(endedMovement))) {
    const pending = plateauGroups.filter((g) => !isOver[phase](g.id));
    if (pending.length) {
      return (
        (phase === endedMovement
          ? pending.find(
              (g) => (g.groupNumber ?? 0) > currentGroup.groupNumber!,
            )
          : null) ?? pending[0]
      );
    }
  }
  return findFirstGroupOnNextPlateau(
    allGroups.filter((g) => !predicates.isGroupDeadliftOver(g.id)),
    currentGroup.plateauNumber,
  );
}

/** Missing future declarations are pending attempts, never implicit forfeits. */
function areAttemptsJudged(
  athletes: AthletesModel[],
  fields: LiftField[],
): boolean {
  return (
    athletes.length > 0 &&
    athletes.every((athlete) =>
      fields.every((field) =>
        [LiftStatus.LIFT, LiftStatus.NO_LIFT].includes(
          athlete[getStatusField(field)] as LiftStatus,
        ),
      ),
    )
  );
}

export function isSquatOver(athletes: AthletesModel[]): boolean {
  if (!athletes?.length) return false;
  // Bench-only groups do not participate in the squat phase.
  if (
    getCompetitionFormatFromAthlete(athletes[0]) ===
    CompetitionFormat.SINGLE_LIFT
  )
    return true;
  return areAttemptsJudged(athletes, ["squat1", "squat2", "squat3"]);
}

export function isBenchOver(athletes: AthletesModel[]): boolean {
  return areAttemptsJudged(athletes, ["bench1", "bench2", "bench3"]);
}

export function isDeadliftOver(athletes: AthletesModel[]): boolean {
  if (!athletes?.length) return false;
  const format = getCompetitionFormatFromAthlete(athletes[0]);
  return areAttemptsJudged(
    athletes,
    getRelevantLiftFields(format) as LiftField[],
  );
}

/**
 * Detects which movement just finished after a lift status update (rising edge).
 * isSquatOver / isBenchOver stay true for the whole next phase, so auto-advancing
 * on the sticky "is over" flag would switch groups on every mid-bench validation.
 * Only return a phase when it flipped from false → true between snapshots.
 */
export function getJustEndedMovement(
  previousAthletes: AthletesModel[],
  nextAthletes: AthletesModel[],
): MovementPhase | null {
  if (!isSquatOver(previousAthletes) && isSquatOver(nextAthletes)) {
    return "squat";
  }
  if (!isBenchOver(previousAthletes) && isBenchOver(nextAthletes)) {
    return "bench";
  }
  if (!isDeadliftOver(previousAthletes) && isDeadliftOver(nextAthletes)) {
    return "deadlift";
  }
  return null;
}

/**
 * Retrieves the athletes who have a non-null weight for the current lift, sorted by weight.
 * @param athletes - An array of AthletesModel representing all the athletes.
 * @param currentLift - The current lift field.
 * @returns An array of AthletesModel representing the athletes with non-null weight for the current lift, sorted by weight.
 */
export function getAthletesByCurrentLiftWeight(
  athletes: AthletesModel[],
  currentLift: LiftField | null,
): AthletesModel[] {
  // Create a copy of the array to avoid mutating the original
  const athletesCopy = [...athletes];

  // If currentLift is null, sort by PDC then lot
  if (currentLift === null) {
    return athletesCopy.sort((a, b) => {
      return a.lot - b.lot;
    });
  }

  // Separate athletes with valid lifts and those with null/undefined lifts
  const athletesWithValidLifts = athletesCopy.filter((a) => {
    const lift = a[currentLift];
    return lift !== null && lift !== undefined && !isNaN(Number(lift));
  });

  const athletesWithNullLifts = athletesCopy.filter((a) => {
    const lift = a[currentLift];
    return lift === null || lift === undefined || isNaN(Number(lift));
  });

  // Sort athletes with valid lifts
  const sortedValidAthletes = athletesWithValidLifts.sort((a, b) => {
    const aWeight = Number(a[currentLift]);
    const bWeight = Number(b[currentLift]);

    if (aWeight !== bWeight) {
      return aWeight - bWeight;
    }
    return a.lot - b.lot;
  });

  // Sort athletes with null lifts
  const sortedNullAthletes = athletesWithNullLifts.sort((a, b) => {
    return a.lot - b.lot;
  });

  // Combine the sorted arrays with valid lifts first, then null lifts
  const result = [...sortedValidAthletes, ...sortedNullAthletes];

  // Log the results for debugging

  return result;
}

/**
 * Remaining announced passages before each athlete's next attempt in the
 * current movement, including the unjudged passage on the platform.
 * Later rounds use the currently declared loads. An undeclared next attempt,
 * a completed movement or an absent athlete has no count.
 */
export function getPassagesBeforeNextAttempt(
  athletes: AthletesModel[],
  currentLift: LiftField | null = getCurrentLift(athletes),
): Record<string, number | null> {
  const counts: Record<string, number | null> = {};
  if (!currentLift) return counts;
  const movement = currentLift.replace(/[123]$/, "");
  let passages = 0;
  for (let round = Number(currentLift.slice(-1)); round <= 3; round++) {
    const field = `${movement}${round}` as LiftField;
    for (const athlete of getAthletesByCurrentLiftWeight(athletes, field)) {
      if (athlete[getStatusField(field)] !== LiftStatus.ANNONCED) continue;
      const load = athlete[field];
      const declared = load != null && Number.isFinite(Number(load));
      if (!Object.prototype.hasOwnProperty.call(counts, athlete.id)) {
        counts[athlete.id] = declared ? passages : null;
      }
      if (declared) passages++;
    }
  }
  return counts;
}

/**
 * Retrieves the next athlete based on the current lift and current athlete.
 * @param athletes - An array of AthletesModel representing all the athletes.
 * @param lift - The current lift field.
 * @returns The next athlete or undefined if there is no next athlete.
 */
export function getAthleteByLift(
  athletes: AthletesModel[],
  lift: LiftField,
): AthletesModel | undefined {
  const orderedAthletes = getAthletesByCurrentLiftWeight(athletes, lift);
  const statusField = getStatusField(lift);
  return orderedAthletes.find(
    (athlete) =>
      athlete[statusField] === LiftStatus.ANNONCED && athlete[lift] !== null,
  );
}

/**
 * Retrieves the athlete at the given index based on the current lift.
 * @param athletes - An array of AthletesModel representing all the athletes.
 * @param index - The index of the athlete.
 * @param currentLift - The current lift field.
 * @returns The athlete at the given index.
 */
export function getAthletesOrderedByLift(athletes: AthletesModel[]) {
  const currentLift = getCurrentLift(athletes);
  return getAthletesByCurrentLiftWeight([...athletes], currentLift ?? "bench1");
}

/**
 * Converts a Decimal or number value to a number, handling null and undefined.
 */
const toNumber = (
  value: Decimal | number | null | undefined,
): number | undefined => {
  if (value === null || value === undefined) return undefined;
  try {
    // Check if value is a Decimal instance
    if (value && typeof value === "object" && "toNumber" in value) {
      const decimalValue = value as Decimal;
      if (typeof decimalValue.toNumber === "function") {
        return decimalValue.toNumber();
      }
    }
    return Number(value);
  } catch (error) {
    console.error("Error converting value to number:", error, value);
    return undefined;
  }
};

export function getAthletesByCategory(
  athletes: AthletesModel[],
  isNational = false,
) {
  // Create a copy of athletes to work with
  let athletesCopy = [...athletes];

  // First sort by categories
  athletesCopy.sort((a, b) => {
    // 1. Disqualified athletes last
    if (a.isDisqualified !== b.isDisqualified) {
      return a.isDisqualified ? 1 : -1;
    }
    // First sort by weight category (from lightest to heaviest based on maxWeight)
    if (a.catPoid?.poidMax !== b.catPoid?.poidMax) {
      return (a.catPoid?.poidMax || 0) - (b.catPoid?.poidMax || 0);
    }
    // Then sort by age category (from youngest to oldest based on maxAge)
    if (!isNational && a.catAge?.ageMax !== b.catAge?.ageMax) {
      return (b.catAge?.ageMax || 0) - (a.catAge?.ageMax || 0);
    }
    // Then sort by discipline
    if (a.disciplineId !== b.disciplineId) {
      return a.disciplineId - b.disciplineId;
    }
    // 1. Total (descending)
    const totalA = toNumber(a.totalkg) || 0;
    const totalB = toNumber(b.totalkg) || 0;

    if (totalA !== totalB) return totalB - totalA;

    // 2. Body weight (descending)
    const pdcA = toNumber(a.pdc) || 0;
    const pdcB = toNumber(b.pdc) || 0;
    if (pdcA !== pdcB) return pdcA - pdcB;

    // 3. Lot number (ascending)
    return a.lot - b.lot;
  });

  // Count each division independently: athletes can be interleaved by display order.
  const placements = new Map<string, number>();
  athletesCopy = athletesCopy.map((athlete) => {
    if (athlete.isDisqualified || !(Number(athlete.totalkg) > 0)) {
      return { ...athlete, placement: 0 };
    }
    const category = getRankingCategoryKey(athlete, isNational);
    const placement = (placements.get(category) ?? 0) + 1;
    placements.set(category, placement);
    return { ...athlete, placement };
  });

  return athletesCopy;
}

/**
 * Retrieves the next lift based on the current lift and athletes' status.
 * @param athletes - An array of AthletesModel representing all the athletes.
 * @param currentLift - The current lift field.
 * @returns The next lift or null if there is no next lift.
 */
export function getNextLift(
  athletes: AthletesModel[],
  currentLift: LiftField,
): LiftField | null {
  if (!athletes || athletes.length === 0) return null;

  const format = getCompetitionFormatFromAthlete(athletes[0]);
  const relevantFields = getRelevantLiftFields(format) as LiftField[];

  athletes = getAthletesByCurrentLiftWeight(athletes, currentLift);
  const index = relevantFields.indexOf(currentLift);

  for (let i = index + 1; i < relevantFields.length; i++) {
    const lift = relevantFields[i];
    if (
      lift &&
      athletes.some(
        (athlete) =>
          athlete[getStatusField(lift)] === LiftStatus.ANNONCED &&
          athlete[lift] !== null,
      )
    ) {
      return lift;
    }
  }
  return null;
}

/**
 * Retrieves the next athlete based on the current lift and current athlete.
 * @param athletes - An array of AthletesModel representing all the athletes.
 * @param currentLift - The current lift field.
 * @param currentAthlete - The current athlete.
 * @returns The next athlete or undefined if there is no next athlete.
 */
/** Minimal group identity for plateau / group-number transitions. */
export type GroupPlateauRef = {
  id: string;
  plateauNumber: number | null;
  groupNumber: number | null;
};

/**
 * Resolves the next lift within the current group, then - when the next lift
 * crosses a movement boundary - looks ahead to the next group on the same
 * plateau (or the first group of the next plateau when every group on the
 * plateau has finished deadlift).
 *
 * Mirrors {@link apps/electron-app/src/displays/loaderdisplay/app.tsx} and
 * auto group selection in {@link apps/electron-app/src/stores/meetStore.ts}.
 */
export function getNextLiftDataWithGroupTransition(params: {
  currentGroup: GroupPlateauRef;
  groups: GroupPlateauRef[];
  groupAthletes: AthletesModel[];
  allAthletes: AthletesModel[];
  currentAthlete: AthletesModel;
  currentLift: LiftField;
}): NextLiftData | undefined {
  const {
    currentGroup,
    groups,
    groupAthletes,
    allAthletes,
    currentAthlete,
    currentLift,
  } = params;

  // Preview the exact same post-judgment snapshot used by actual rotation.
  const after = allAthletes.map((athlete) =>
    athlete.id === currentAthlete.id
      ? { ...athlete, [getStatusField(currentLift)]: LiftStatus.LIFT }
      : athlete,
  );
  const afterGroup = after.filter((a) => a.group_id === currentGroup.id);
  const ended = getJustEndedMovement(groupAthletes, afterGroup);
  const rows = (id: string) => after.filter((a) => a.group_id === id);
  const target = ended
    ? resolveNextCompetitionGroup(groups, currentGroup, ended, {
        isGroupSquatOver: (id) => isSquatOver(rows(id)),
        isGroupBenchOver: (id) => isBenchOver(rows(id)),
        isGroupDeadliftOver: (id) => isDeadliftOver(rows(id)),
        isGroupEmpty: (id) => rows(id).length === 0,
      })
    : currentGroup;
  if (!target) return undefined;
  const roster = rows(target.id);
  const lift = getCurrentLift(roster);
  const athlete = lift ? getAthleteByLift(roster, lift) : null;
  return athlete && lift ? { athlete, lift } : undefined;
}

export function getNextAthlete(
  athletes: AthletesModel[],
  currentLift: LiftField,
  currentAthlete: AthletesModel,
): NextLiftData | undefined {
  let sortedAthletes = getAthletesByCurrentLiftWeight(athletes, currentLift);
  const filteredAthletes = sortedAthletes.filter(
    (a) => a[getStatusField(currentLift)] === LiftStatus.ANNONCED,
  );
  let currentIndex = filteredAthletes.findIndex(
    (athlete) => athlete.id === currentAthlete.id,
  );
  if (currentIndex + 1 < filteredAthletes.length) {
    return {
      athlete: filteredAthletes[currentIndex + 1],
      lift: currentLift,
    };
  } else {
    let nextLift = getNextLift(athletes, currentLift);
    return {
      athlete: getAthleteByLift(athletes, nextLift ?? "bench1"),
      lift: nextLift,
    };
  }
}

/**
 * Gets the best successful attempt out of the first three attempts for a given lift type
 */
export const getBest3Lift = (
  athlete: AthletesModel,
  liftType: "squat" | "bench" | "deadlift",
): number | undefined => {
  const attempts = [1, 2, 3]
    .map((i) => {
      const field = `${liftType}${i}` as keyof AthletesModel;
      const statusField = getStatusField(field);
      const value = athlete[field];
      const status = athlete[statusField];
      return status === LiftStatus.LIFT ? Number(value) : undefined;
    })
    .filter((v) => v !== undefined) as number[];

  return attempts.length > 0 ? Math.max(...attempts) : undefined;
};

/**
 * Checks if an athlete has failed all three attempts for a specific lift type (squat, bench, or deadlift)
 * @param athlete - The athlete to check
 * @param liftType - The type of lift to check ('squat', 'bench', or 'deadlift')
 * @returns True if all three attempts were 'no lift', false otherwise
 */
export const hasFailedAllAttempts = (
  athlete: AthletesModel,
  liftType: "squat" | "bench" | "deadlift",
): boolean => {
  // Get all three attempts for the specified lift type
  const attempts = [1, 2, 3].map((i) => {
    const statusField = getStatusField(`${liftType}${i}` as LiftField);
    return athlete[statusField];
  });
  // Check if all attempts are 'no lift'
  return attempts.every((status) => status === LiftStatus.NO_LIFT);
};

/**
 * Checks if an athlete has failed all attempts for any lift type (squat, bench, or deadlift)
 * @param athlete - The athlete to check
 * @returns True if the athlete has failed all attempts for at least one lift type, false otherwise
 */
export const hasFailedAnyLift = (athlete: AthletesModel): boolean => {
  const format = getCompetitionFormatFromAthlete(athlete);
  if (format === CompetitionFormat.SINGLE_LIFT) {
    return false;
  }
  // Check each lift type (squat, bench, deadlift)
  return ["squat", "bench", "deadlift"].some((liftType) =>
    hasFailedAllAttempts(athlete, liftType as "squat" | "bench" | "deadlift"),
  );
};

/**
 * Returns the next attempt field within the same movement for ticket-time display.
 * Only attempts 1 and 2 produce a target (N+1); attempt 3 returns null
 * (no ticket time across movement boundaries, e.g. bench3 → deadlift1).
 *
 * @param lift - The lift that was just judged.
 * @returns The next attempt field, or null if no ticket timer should start.
 * @example
 * getTicketTargetLiftField("squat1"); // "squat2"
 * getTicketTargetLiftField("bench3"); // null
 */
export function getTicketTargetLiftField(lift: LiftField): LiftField | null {
  const match = /^(squat|bench|deadlift)([123])$/.exec(lift);
  if (!match) return null;
  const movement = match[1] as "squat" | "bench" | "deadlift";
  const attempt = Number(match[2]);
  // Only attempt 1→2 and 2→3 get a ticket timer
  if (attempt < 1 || attempt > 2) return null;
  return `${movement}${attempt + 1}` as LiftField;
}
